import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { catalogApi } from "../../../api/catalog";
import { newsApi, type ArticleDetail, type ArticleTranslation } from "../../../api/news";
import type { ApiError } from "../../../api/client";
import { ALL_ROLES } from "../../../auth/roles";
import { Select } from "../../../components/Select";

const LOCALES = ["sr", "en", "de", "hr", "sl", "it", "es", "fr", "ru", "tr"] as const;

/**
 * Article editor — deliberately minimal. Body is a textarea with raw HTML; the
 * server runs OWASP HTML sanitizer on persist. A rich-text editor (TipTap)
 * is deliberately left out to keep this CRUD lean.
 */
export default function AdminNewsEditorPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [articleId, setArticleId] = useState<string | null>(null);
  const [slug, setSlug] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [coverKey, setCoverKey] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [visibleRoles, setVisibleRoles] = useState<string[]>([...ALL_ROLES]);
  const [translations, setTranslations] = useState<Record<string, ArticleTranslation>>({
    en: { title: "", excerpt: null, bodyHtml: null },
  });
  const [error, setError] = useState<string | null>(null);
  const [savingMsg, setSavingMsg] = useState<string | null>(null);

  // Categories from the article-news endpoint don't exist; for now we surface
  // catalog categories so admins at least have something to pick from. A
  // dedicated /api/news/categories endpoint can come later.
  useEffect(() => {
    void catalogApi.tree("en").then((tree) => {
      const flat: { id: string; name: string }[] = [];
      const walk = (ns: typeof tree, depth = 0) => ns.forEach((n) => {
        flat.push({ id: n.id, name: `${"— ".repeat(depth)}${n.name}` });
        walk(n.children, depth + 1);
      });
      walk(tree);
      setCategories(flat);
    });
  }, []);

  useEffect(() => {
    if (isNew || !id) return;
    void newsApi.get(id).then((a: ArticleDetail) => {
      setArticleId(a.id);
      setSlug(a.slug);
      setCategoryId(a.categoryId);
      setCoverKey(a.coverMinioKey ?? "");
      setScheduledAt(a.scheduledPublishAt ? a.scheduledPublishAt.slice(0, 16) : "");
      setVisibleRoles(a.visibleToRoles);
      setTranslations(a.translations);
    }).catch((e: ApiError) => setError(e.message));
  }, [id, isNew]);

  function toggleRole(r: string) {
    setVisibleRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSavingMsg(null);
    // Select is a <button>, so it carries no native `required`.
    if (!categoryId) {
      setError(t("admin.news.editor.categoryRequired"));
      return;
    }
    try {
      const payload = {
        slug,
        categoryId,
        coverMinioKey: coverKey || undefined,
        visibleToRoles: visibleRoles,
        scheduledPublishAt: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
        translations,
      };
      if (isNew) {
        if (!translations.en?.title?.trim()) { setError(t("admin.news.editor.error.englishTitleRequired")); return; }
        const created = await newsApi.create(payload);
        navigate(`/admin/news/${created.id}`, { replace: true });
      } else if (articleId) {
        await newsApi.update(articleId, payload);
        setSavingMsg(t("admin.news.editor.toast.saved"));
      }
    } catch (err) {
      setError((err as ApiError).message);
    }
  }

  return (
    <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 900 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>
          {isNew ? t("admin.news.editor.title.new") : t("admin.news.editor.title.edit", { slug })}
        </h1>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={() => navigate(-1)} style={secondary}>{t("admin.news.editor.action.back")}</button>
          <button type="submit" style={primary}>{isNew ? t("admin.news.editor.action.create") : t("admin.news.editor.action.save")}</button>
        </div>
      </header>

      {error && <div style={errorBox}>{error}</div>}
      {savingMsg && <div style={successBox}>{savingMsg}</div>}

      <section style={card}>
        <Field label={t("admin.news.editor.field.slug")} required>
          <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} pattern="^[a-z0-9-]{2,120}$" required style={input} />
        </Field>
        <Field label={t("admin.news.editor.field.category")} required>
          <Select<string>
            value={categoryId}
            onChange={setCategoryId}
            ariaLabel={t("admin.news.editor.field.category")}
            options={[
              { value: "", label: t("admin.news.editor.field.categoryPlaceholder") },
              ...categories.map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
        </Field>
        <Field label={t("admin.news.editor.field.coverKey")}>
          <input value={coverKey} onChange={(e) => setCoverKey(e.target.value)} placeholder={t("admin.news.editor.field.coverKeyPlaceholder")} style={input} />
        </Field>
        <Field label={t("admin.news.editor.field.scheduledAt")}>
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            style={input}
          />
        </Field>
        <Field label={t("admin.news.editor.field.visibleRoles")}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
            {ALL_ROLES.map((r) => (
              <label key={r} style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <input type="checkbox" checked={visibleRoles.includes(r)} onChange={() => toggleRole(r)} />
                {r}
              </label>
            ))}
          </div>
        </Field>
      </section>

      <section style={card}>
        <h2 style={subTitle}>{t("admin.news.editor.section.translations")}</h2>
        {LOCALES.map((loc) => {
          const tr = translations[loc] ?? { title: "", excerpt: null, bodyHtml: null };
          return (
            <div key={loc} style={{ borderBottom: "1px solid var(--border)", padding: "12px 0" }}>
              <strong style={{ textTransform: "uppercase", fontSize: 13 }}>{loc}</strong>
              <Field label={t("admin.news.editor.field.translationTitle")} required={loc === "en"}>
                <input
                  value={tr.title}
                  onChange={(e) => setTranslations({ ...translations, [loc]: { ...tr, title: e.target.value } })}
                  required={loc === "en"}
                  style={input}
                />
              </Field>
              <Field label={t("admin.news.editor.field.translationExcerpt")}>
                <textarea
                  value={tr.excerpt ?? ""}
                  onChange={(e) => setTranslations({ ...translations, [loc]: { ...tr, excerpt: e.target.value } })}
                  rows={2}
                  style={{ ...input, fontFamily: "inherit", resize: "vertical" }}
                />
              </Field>
              <Field label={t("admin.news.editor.field.translationBody")}>
                <textarea
                  value={tr.bodyHtml ?? ""}
                  onChange={(e) => setTranslations({ ...translations, [loc]: { ...tr, bodyHtml: e.target.value } })}
                  rows={10}
                  style={{ ...input, fontFamily: "ui-monospace, monospace", resize: "vertical", fontSize: 12 }}
                />
              </Field>
            </div>
          );
        })}
      </section>
    </form>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, marginTop: 8 }}>
      <span style={{ color: "var(--ink-3)" }}>
        {label} {required && <span style={{ color: "var(--err)" }}>*</span>}
      </span>
      {children}
    </label>
  );
}

const card: React.CSSProperties = { background: "var(--surface)", padding: 20, borderRadius: 10, boxShadow: "var(--shadow-1)" };
const subTitle: React.CSSProperties = { margin: "0 0 8px", fontSize: 14, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.5 };
const input: React.CSSProperties = { padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border-strong)", fontSize: 14, width: "100%" };
const primary: React.CSSProperties = { padding: "8px 14px", background: "var(--brand-primary-600)", color: "var(--on-primary)", border: "none", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const secondary: React.CSSProperties = { padding: "8px 14px", background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const errorBox: React.CSSProperties = { padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const successBox: React.CSSProperties = { padding: 10, background: "var(--ok-bg)", color: "var(--ok)", borderRadius: 6, fontSize: 13 };
